import { Button, Col, Row, Space, Avatar, Popover, Divider, message } from "antd";
import Modal from "antd/lib/modal/Modal";
import { ExchangeBoardProperties, EExchangeBoardType } from "../../../types/exchangeBoard.type";
import UserOutlined from "@ant-design/icons/lib/icons/UserOutlined";
import { CalendarOutlined, EnvironmentOutlined, LinkOutlined } from "@ant-design/icons";
import moment from "moment";
import { doWhatsAppExchangeBoard } from "../../../helpers/exchangeBoard";
import { FacebookShareButton, WhatsappShareButton, TelegramShareButton, TwitterShareButton, LineShareButton, FacebookIcon, WhatsappIcon, TelegramIcon, XIcon, LineIcon } from 'react-share';
import { useState, useEffect } from "react";
import { httpRequest } from "../../../helpers/api";

const formatNumberWithDots = (num: number | undefined | null): string => {
    if (num === undefined || num === null) return '';
    if (num === 0) return '0';
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

const priceShorten = (price: number, lng: string = "id") => {
    const thousand = lng === "en" ? "K" : "Rb";
    const million = lng === "en" ? "M" : "Jt";
    const billion = lng === "en" ? "B" : "M";

    if (price >= 1000000000) {
        return `${(price / 1000000000).toFixed(price % 1000000000 === 0 ? 0 : 1)} ${billion}`;
    }
    if (price >= 1000000) {
        return `${(price / 1000000).toFixed(price % 1000000 === 0 ? 0 : 1)} ${million}`;
    }
    if (price >= 1000) {
        return `${(price / 1000).toFixed(price % 1000 === 0 ? 0 : 1)} ${thousand}`;
    }
    return price.toString();
};

interface DetailExchangeBoardModalProps {
    open: boolean;
    onClose: () => void;
    exchangeBoard?: ExchangeBoardProperties;
}

const DetailExchangeBoardModal: React.FC<DetailExchangeBoardModalProps> = ({
    open,
    onClose,
    exchangeBoard
}) => {
    const [areaName, setAreaName] = useState<string>("");

    useEffect(() => {
        const fetchAreaName = async () => {
            if (exchangeBoard?.cityGroupId) {
                try {
                    const { data } = await httpRequest.get(`group-of-city/${exchangeBoard.cityGroupId}`);
                    setAreaName((data as any).payload.cityGroupName || "");
                } catch (err) {
                    console.error("Failed to fetch area name", err);
                    setAreaName("");
                }
            } else {
                setAreaName("");
            }
        };

        if (open && exchangeBoard) {
            fetchAreaName();
        }
    }, [open, exchangeBoard]);

    if (!exchangeBoard) return null;

    const typeConfig: Record<EExchangeBoardType, { label: string; bgColor: string; textColor: string }> = {
        [EExchangeBoardType.WTB]: { label: 'WTB', bgColor: '#52C41A', textColor: 'white' },
        [EExchangeBoardType.WTR]: { label: 'WTR', bgColor: '#366CF4', textColor: 'white' },
        [EExchangeBoardType.FOR_SALE]: { label: 'For Sale', bgColor: '#e6f7ff', textColor: '#1890ff' },
        [EExchangeBoardType.FOR_RENT]: { label: 'For Rent', bgColor: '#fff1f0', textColor: '#ff4d4f' }
    };
    const config = typeConfig[exchangeBoard.type];
    const host = window.location.protocol + "//" + window.location.host;
    const shareUrl = `${host}/request-board?id=${exchangeBoard.exchangeBoardId}`;

    const listingType = exchangeBoard.type === EExchangeBoardType.WTB ? "Buyer" : exchangeBoard.type === EExchangeBoardType.WTR ? "Renter" : "Listing";
    const location = areaName || exchangeBoard.city || "";

    const contentText =
        `Hot ${listingType} - ${location}!\n\n` +
        `${exchangeBoard.titlePlainText || "-"}\n` +
        `LT/LB: ${exchangeBoard.landSize || "-"}/${exchangeBoard.buildingSize || "-"} m²\n` +
        `${exchangeBoard.budget ? `Price: Rp ${priceShorten(exchangeBoard.budget)}` : ""}\n\n` +
        `Contact your trusted agent:\n` +
        `${exchangeBoard.picUser?.nickName || exchangeBoard.picUser?.name} - ${exchangeBoard.picProfile?.firstPhoneNumber ?? "Not Set"}\n\n` +
        `Cek detail lengkap di sini:\n`;

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={
                <div style={{ display: "flex", gap: "12px", width: "100%" }}>
                    <Popover
                        placement="top"
                        trigger="click"
                        overlayInnerStyle={{
                            border: "2px solid #E3E9F4",
                            borderRadius: "10px",
                            padding: "4px",
                        }}
                        content={
                            <Space align="start">
                                <FacebookShareButton url={shareUrl} title={contentText}>
                                    <FacebookIcon size={32} round />
                                </FacebookShareButton>
                                <WhatsappShareButton url={shareUrl} title={contentText}>
                                    <WhatsappIcon size={32} round />
                                </WhatsappShareButton>
                                <TelegramShareButton url={shareUrl} title={contentText}>
                                    <TelegramIcon size={32} round />
                                </TelegramShareButton>
                                <TwitterShareButton url={shareUrl} title={contentText}>
                                    <XIcon size={32} round />
                                </TwitterShareButton>
                                <LineShareButton url={shareUrl} title={contentText}>
                                    <LineIcon size={32} round />
                                </LineShareButton>
                                <Button
                                    icon={<LinkOutlined />}
                                    shape="circle"
                                    style={{
                                        width: 32,
                                        height: 32,
                                        minWidth: 32,
                                        padding: 0,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        backgroundColor: '#0088cc',
                                        borderColor: '#0088cc',
                                        color: 'white'
                                    }}
                                    onClick={() => {
                                        navigator.clipboard.writeText(shareUrl);
                                        message.success('Link copied to clipboard!');
                                    }}
                                />
                            </Space>
                        }
                    >
                        <Button style={{ flex: 1, padding: "12px 0" }}>
                            Share
                        </Button>
                    </Popover>

                    <Button
                        type="primary"
                        style={{ flex: 1, padding: "12px 0", margin: 0 }}
                        onClick={() => doWhatsAppExchangeBoard(exchangeBoard, areaName)}
                        disabled={exchangeBoard.isExpired}
                    >
                        Contact
                    </Button>
                </div>
            }
            width={600}
            title="Detail Board"
        >
            <Row gutter={12} style={{ marginBottom: 12 }}>
                <Col flex="48px">
                    <Avatar
                        size={48}
                        src={exchangeBoard.picUser?.profilePic}
                        icon={<UserOutlined />}
                        style={{ backgroundColor: '#1890ff' }}
                    />
                </Col>
                <Col flex="auto" >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                            <div style={{ fontWeight: 600, fontSize: '15px', color: '#000', marginBottom: 2 }}>
                                {exchangeBoard.picUser?.nickName?.toUpperCase() || exchangeBoard.picUser?.name || 'REMAX'}
                            </div>
                            <div style={{ fontSize: '12px', color: '#999' }}>
                                {exchangeBoard.picEmployee?.office?.officeName || exchangeBoard.picOffice?.officeName || "REMAX"}
                            </div>
                        </div>
                        <div
                            style={{
                                padding: '4px 12px',
                                borderRadius: '16px',
                                backgroundColor: config.bgColor,
                                color: config.textColor,
                                fontSize: '12px',
                                fontWeight: 600
                            }}
                        >
                            {config.label}
                        </div>
                    </div>
                </Col>
            </Row>

            <Row gutter={14} style={{ marginBottom: 12, display: 'flex', flexDirection: 'row', gap: 8 }}>
                <Col style={{ borderRadius: '100px', backgroundColor: '#F2F6FD', padding: '0 8px', marginLeft: 8 }}>
                    <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                        <EnvironmentOutlined />
                        <span>{areaName || exchangeBoard.city || "Area"}</span>
                    </Space>
                </Col>
                <Col style={{ borderRadius: '100px', backgroundColor: '#F2F6FD', padding: '0 8px' }}>
                    <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                        <img src="/images/bookmark.svg" alt="property type" style={{ width: 14, height: 14 }} />
                        <span>{exchangeBoard.listingPropertyType?.name || "Rumah"}</span>
                    </Space>
                </Col>
                <Col style={{ borderRadius: '100px', backgroundColor: '#F2F6FD', padding: '0 8px' }}>
                    <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                        <CalendarOutlined />
                        <span>{moment(exchangeBoard.createdAt).format('DD MMM YYYY, HH:mm')}</span>
                    </Space>
                </Col>
            </Row>

            <Divider type="horizontal" style={{ marginBottom: 12 }} />

            <Row gutter={14} style={{ marginBottom: 12, display: 'flex', flexDirection: 'row', gap: 8 }}>
                {exchangeBoard.landSize !== undefined && exchangeBoard.landSize !== null && (
                    <Col>
                        <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                            <div
                                style={{
                                    width: 20,
                                    height: 20,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#E3E9F4",
                                    borderRadius: "4px",
                                }}
                            >
                                <img
                                    src="/images/landSize.svg"
                                    alt="land size"
                                    style={{ width: 12, height: 12 }}
                                />
                            </div>
                            <span>{formatNumberWithDots(exchangeBoard.landSize)} (m²)</span>
                        </Space>
                    </Col>
                )}
                {exchangeBoard.buildingSize !== undefined && exchangeBoard.buildingSize !== null && (
                    <Col>
                        <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                            <div
                                style={{
                                    width: 20,
                                    height: 20,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#E3E9F4",
                                    borderRadius: "4px",
                                }}
                            >
                                <img
                                    src="/images/buildingSize.svg"
                                    alt="building size"
                                    style={{ width: 12, height: 12 }}
                                />
                            </div>
                            <span>{formatNumberWithDots(exchangeBoard.buildingSize)} (m²)</span>
                        </Space>
                    </Col>
                )}
                {exchangeBoard.budget && (
                    <Col>
                        <Space size={4} style={{ fontSize: '12px', color: 'black' }}>
                            <div
                                style={{
                                    width: 20,
                                    height: 20,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center",
                                    backgroundColor: "#E3E9F4",
                                    borderRadius: "4px",
                                }}
                            >
                                <img
                                    src="/images/wallet.svg"
                                    alt="budget"
                                    style={{ width: 12, height: 12 }}
                                />
                            </div>
                            <span>
                                {priceShorten(exchangeBoard.budget)}
                            </span>
                        </Space>
                    </Col>
                )}
            </Row>

            <Divider type="horizontal" style={{ marginBottom: 12 }} />

            <div
                style={{
                    fontSize: '13px',
                    color: '#333',
                    lineHeight: '1.6',
                    marginBottom: '16px',
                    whiteSpace: 'pre-wrap'
                }}
                dangerouslySetInnerHTML={{ __html: (exchangeBoard.title || '').replace(/<p>/g, '<p style="margin:0;padding:0">') }}
            />
        </Modal >
    );
};

export default DetailExchangeBoardModal;